/**
 * Mermaid 图表渲染组件
 * 支持图表/代码切换、缩放、下载（SVG/PNG/代码）、全屏
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import mermaid from 'mermaid';
import { preprocessMermaidCode } from '../utils/mermaidPreprocessor';

// ---- SVG 图标 ----
const ChartIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" /><path d="M9 21V9" />
  </svg>
);
const CodeIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
  </svg>
);
const ZoomOutIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);
const ZoomInIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);
const DownloadIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const FullscreenIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" /><line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
  </svg>
);
const CopyIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);
const CheckIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ---- Mermaid 配置 ----
const getMermaidConfig = (): any => ({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'inherit',
  suppressErrorRendering: true,
});

mermaid.initialize(getMermaidConfig());

// ---- 模块级状态 ----
const mermaidUserViewPrefs = new Map<string, 'code' | 'chart'>();
const getChartKey = (chart: string): string =>
  `${chart.length}-${chart.substring(0, 30)}`;

interface MermaidBlockProps {
  chart: string;
  defaultView?: 'code' | 'chart';
}

const MermaidBlock: React.FC<MermaidBlockProps> = React.memo(({ chart, defaultView = 'code' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const codeBlockRef = useRef<HTMLPreElement>(null);
  const idRef = useRef(`mermaid-${Math.random().toString(36).substring(2, 9)}`);
  const chartKeyRef = useRef(getChartKey(chart));
  const [showCode, setShowCode] = useState(() => {
    const key = getChartKey(chart);
    chartKeyRef.current = key;
    const userPref = mermaidUserViewPrefs.get(key);
    if (userPref !== undefined) return userPref === 'code';
    return defaultView === 'code';
  });
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const fullscreenRef = useRef<HTMLDivElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const newKey = getChartKey(chart);
    if (newKey !== chartKeyRef.current) {
      mermaidUserViewPrefs.set(newKey, showCode ? 'code' : 'chart');
      mermaidUserViewPrefs.delete(chartKeyRef.current);
      chartKeyRef.current = newKey;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  const toggleView = useCallback((code: boolean) => {
    mermaidUserViewPrefs.set(chartKeyRef.current, code ? 'code' : 'chart');
    setShowCode(code);
  }, []);

  const processedChart = preprocessMermaidCode(chart);

  const containerKeyRef = useRef(0);
  const prevChartRef = useRef(chart);
  if (chart !== prevChartRef.current) {
    prevChartRef.current = chart;
    containerKeyRef.current += 1;
  }
  const containerKey = containerKeyRef.current;

  useEffect(() => { setRenderError(null); }, [chart]);

  const lastSvgRef = useRef<string>('');
  const activeRenderIdRef = useRef<string>('');
  const renderCounterRef = useRef(0);

  useEffect(() => {
    if (!containerRef.current || showCode) return;
    const containerEl = containerRef.current;

    const safeKey = chartKeyRef.current.replace(/[^a-zA-Z0-9_-]/g, '_');
    renderCounterRef.current += 1;
    const renderId = `mermaid-${safeKey}-${renderCounterRef.current}`;
    activeRenderIdRef.current = renderId;

    containerRef.current.innerHTML = '';
    document.querySelectorAll('[id^="dmermaid-"]').forEach((el) => {
      if (el.parentElement === document.body) el.remove();
    });
    setRenderError(null);
    try { mermaid.initialize(getMermaidConfig()); } catch { /* ignore */ }

    let cancelled = false;
    (async () => {
      await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
      if (cancelled) return;
      mermaid.render(renderId, processedChart)
        .then(({ svg }) => {
          if (cancelled) return;
          containerEl.innerHTML = svg;
          lastSvgRef.current = svg;
          setRenderError(null);
        })
        .catch((err: any) => {
          if (cancelled) return;
          console.error('Mermaid render error:', err);
          try { mermaid.initialize(getMermaidConfig()); } catch { /* ignore */ }
          setRenderError(err.message || String(err));
        });
    })();

    return () => {
      cancelled = true;
      const idToClean = renderId;
      requestAnimationFrame(() => {
        const tmpDiv = document.getElementById(`d${idToClean}`);
        if (tmpDiv) tmpDiv.remove();
      });
    };
  }, [chart, showCode, isFullscreen]);

  const zoomOut = useCallback(() => setScale((s) => Math.max(0.3, +(s - 0.15).toFixed(2))), []);
  const zoomIn = useCallback(() => setScale((s) => Math.min(3, +(s + 0.15).toFixed(2))), []);

  const copyCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(chart);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = chart;
      textarea.style.position = 'fixed'; textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select(); document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [chart]);

  const downloadSVG = useCallback(() => {
    const svgEl = containerRef.current?.querySelector('svg');
    if (!svgEl) return;
    const clone = svgEl.cloneNode(true) as SVGElement;
    const svgData = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `mermaid-chart-${idRef.current}.svg`; a.click();
    URL.revokeObjectURL(url);
    setShowDownloadMenu(false);
  }, []);

  const downloadPNG = useCallback(() => {
    const svgEl = containerRef.current?.querySelector('svg');
    if (!svgEl) return;
    const clone = svgEl.cloneNode(true) as SVGElement;
    let svgWidth = 0, svgHeight = 0;
    const viewBox = clone.getAttribute('viewBox');
    if (viewBox) {
      const parts = viewBox.split(/\s+/);
      if (parts.length === 4) { svgWidth = parseFloat(parts[2]); svgHeight = parseFloat(parts[3]); }
    }
    if (!svgWidth || !svgHeight) {
      const w = clone.getAttribute('width'), h = clone.getAttribute('height');
      if (w) svgWidth = parseFloat(w); if (h) svgHeight = parseFloat(h);
    }
    if (!svgWidth || !svgHeight) { svgWidth = 800; svgHeight = 600; }
    clone.setAttribute('width', String(svgWidth)); clone.setAttribute('height', String(svgHeight));
    const svgData = new XMLSerializer().serializeToString(clone);
    const dataUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgData);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const s = 2; canvas.width = svgWidth * s; canvas.height = svgHeight * s;
      const ctx = canvas.getContext('2d')!;
      ctx.scale(s, s); ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, svgWidth, svgHeight);
      ctx.drawImage(img, 0, 0, svgWidth, svgHeight);
      canvas.toBlob((blob) => {
        if (!blob) return;
        const pngUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = pngUrl; a.download = `mermaid-chart-${idRef.current}.png`; a.click();
        URL.revokeObjectURL(pngUrl);
      }, 'image/png');
    };
    img.onerror = () => console.error('Failed to load SVG for PNG conversion');
    img.src = dataUrl;
    setShowDownloadMenu(false);
  }, []);

  const downloadCode = useCallback(() => {
    const blob = new Blob([chart], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `mermaid-chart-${idRef.current}.txt`; a.click();
    URL.revokeObjectURL(url);
    setShowDownloadMenu(false);
  }, [chart]);

  useEffect(() => {
    if (!showDownloadMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target as Node))
        setShowDownloadMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDownloadMenu]);

  const enterFullscreen = useCallback(() => setIsFullscreen(true), []);
  const exitFullscreen = useCallback(() => setIsFullscreen(false), []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && isFullscreen) setIsFullscreen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isFullscreen]);

  const toolbar = (
    <div className="flex items-center justify-between bg-gray-100 border border-gray-200 rounded-t-md px-2 py-1.5 select-none">
      <div className="flex items-center bg-white rounded border border-gray-300 overflow-hidden">
        <button onClick={() => toggleView(false)} className={`flex items-center gap-1 px-2.5 py-1 text-xs transition-colors ${!showCode ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`} title="显示图表"><ChartIcon /><span>图表</span></button>
        <button onClick={() => toggleView(true)} className={`flex items-center gap-1 px-2.5 py-1 text-xs transition-colors ${showCode ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`} title="显示代码"><CodeIcon /><span>代码</span></button>
      </div>
      <div className="flex items-center gap-1">
        {!showCode && (<>
          <button onClick={zoomOut} disabled={scale <= 0.3} className="p-1 rounded text-gray-500 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="缩小"><ZoomOutIcon /></button>
          <span className="text-xs text-gray-500 font-mono min-w-[40px] text-center">{Math.round(scale * 100)}%</span>
          <button onClick={zoomIn} disabled={scale >= 3} className="p-1 rounded text-gray-500 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="放大"><ZoomInIcon /></button>
          <div className="w-px h-5 bg-gray-300 mx-0.5" />
        </>)}
        {showCode && (<>
          <button onClick={copyCode} className={`p-1 rounded transition-colors ${copied ? 'text-green-600 bg-green-50' : 'text-gray-500 hover:bg-gray-200 hover:text-gray-700'}`} title="复制代码">{copied ? <CheckIcon /> : <CopyIcon />}</button>
          <div className="w-px h-5 bg-gray-300 mx-0.5" />
        </>)}
        <div className="relative" ref={downloadMenuRef}>
          <button onClick={() => setShowDownloadMenu(!showDownloadMenu)} className="p-1 rounded text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors" title="下载"><DownloadIcon /></button>
          {showDownloadMenu && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-40 py-1 min-w-[120px]">
              {!showCode && (<>
                <button onClick={downloadSVG} className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"><svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>下载 SVG</button>
                <button onClick={downloadPNG} className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"><svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>下载 PNG</button>
                <div className="border-t border-gray-100 my-0.5" />
              </>)}
              <button onClick={downloadCode} className="w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"><svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>下载代码</button>
            </div>
          )}
        </div>
        <button onClick={enterFullscreen} className="p-1 rounded text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors" title="全屏"><FullscreenIcon /></button>
      </div>
    </div>
  );

  const chartContent = (
    <div className="flex justify-center overflow-x-auto p-4 bg-white"
      style={renderError ? undefined : { transform: `scale(${scale})`, transformOrigin: 'top center', transition: 'transform 0.15s ease' }}>
      <div className="w-full bg-red-50 border border-red-200 rounded p-3 text-left" style={{ display: renderError ? 'block' : 'none' }}>
        <p className="text-red-600 text-xs font-medium mb-1">Mermaid 渲染错误</p>
        <pre className="text-red-500 text-xs whitespace-pre-wrap font-mono">{renderError}</pre>
      </div>
      <div key={containerKey} ref={containerRef} className="inline-block max-w-full" style={{ display: renderError ? 'none' : 'inline-block' }} />
    </div>
  );

  const codeContent = (
    <pre ref={codeBlockRef} className="bg-gray-50 p-4 overflow-x-auto text-sm font-mono text-gray-700 whitespace-pre m-0 rounded-b-md">{chart}</pre>
  );

  if (isFullscreen) {
    return (
      <div ref={fullscreenRef} className="fixed inset-0 z-50 bg-black/80 flex flex-col" onClick={(e) => { if (e.target === e.currentTarget) exitFullscreen(); }}>
        <div className="flex items-center justify-between bg-gray-900 border-b border-gray-700 px-4 py-2">
          <span className="text-gray-300 text-xs font-mono">mermaid</span>
          <div className="flex items-center gap-2">
            {!showCode && (<>
              <button onClick={zoomOut} disabled={scale <= 0.3} className="p-1.5 rounded text-gray-400 hover:bg-gray-700 hover:text-white disabled:opacity-30 transition-colors" title="缩小"><ZoomOutIcon /></button>
              <span className="text-xs text-gray-400 font-mono min-w-[40px] text-center">{Math.round(scale * 100)}%</span>
              <button onClick={zoomIn} disabled={scale >= 3} className="p-1.5 rounded text-gray-400 hover:bg-gray-700 hover:text-white disabled:opacity-30 transition-colors" title="放大"><ZoomInIcon /></button>
              <div className="w-px h-5 bg-gray-600 mx-1" />
            </>)}
            {showCode && (<>
              <button onClick={copyCode} className={`p-1.5 rounded transition-colors ${copied ? 'text-green-400 bg-green-900/50' : 'text-gray-400 hover:bg-gray-700 hover:text-white'}`} title="复制代码">{copied ? <CheckIcon /> : <CopyIcon />}</button>
              <div className="w-px h-5 bg-gray-600 mx-1" />
            </>)}
            <div className="relative" ref={downloadMenuRef}>
              <button onClick={() => setShowDownloadMenu(!showDownloadMenu)} className="p-1.5 rounded text-gray-400 hover:bg-gray-700 hover:text-white transition-colors" title="下载"><DownloadIcon /></button>
              {showDownloadMenu && (
                <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-md shadow-xl z-50 py-1 min-w-[120px]">
                  {!showCode && (<>
                    <button onClick={downloadSVG} className="w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700 flex items-center gap-2"><svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>下载 SVG</button>
                    <button onClick={downloadPNG} className="w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700 flex items-center gap-2"><svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>下载 PNG</button>
                    <div className="border-t border-gray-700 my-0.5" />
                  </>)}
                  <button onClick={downloadCode} className="w-full text-left px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-700 flex items-center gap-2"><svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>下载代码</button>
                </div>
              )}
            </div>
            <button onClick={exitFullscreen} className="p-1.5 rounded text-gray-400 hover:bg-gray-700 hover:text-white transition-colors" title="退出全屏">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 8 4 4 8 4" /><polyline points="16 20 20 20 20 16" /><line x1="4" y1="4" x2="9" y2="9" /><line x1="20" y1="20" x2="15" y2="15" /></svg>
            </button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center overflow-auto p-8">
          {showCode ? (
            <div className="bg-white rounded-lg shadow-2xl p-6 max-w-4xl w-full max-h-full overflow-auto">
              <pre className="text-sm font-mono text-gray-700 whitespace-pre-wrap">{chart}</pre>
            </div>
          ) : (<>
            <div className="bg-white rounded-lg shadow-2xl p-6 max-w-2xl w-full" style={{ display: renderError ? 'block' : 'none' }}>
              <p className="text-red-600 text-sm font-medium mb-2">Mermaid 渲染错误</p>
              <pre className="text-red-500 text-xs whitespace-pre-wrap font-mono">{renderError}</pre>
            </div>
            <div className="bg-white rounded-lg shadow-2xl p-8" style={{ display: renderError ? 'none' : 'block', transform: `scale(${scale})`, transformOrigin: 'center center', transition: 'transform 0.15s ease' }}>
              <div key={containerKey} ref={containerRef} className="inline-block" />
            </div>
          </>)}
        </div>
      </div>
    );
  }

  return (
    <div className="my-4 border border-gray-200 rounded-md">
      {toolbar}
      {showCode ? codeContent : chartContent}
    </div>
  );
});

MermaidBlock.displayName = 'MermaidBlock';

export default MermaidBlock;
